/**
 * Is this the INSTALLED app, or a browser tab? (DECISIONS D-019)
 *
 * ── The platform fact this exists for ───────────────────────────────────────
 * iOS gives a Home Screen web app its own storage container, separate from
 * Safari's. Same origin, same URL, same phone — different IndexedDB, different
 * localStorage, different service-worker cache. Nothing is shared and nothing
 * migrates.
 *
 * Observed 2026-08-06: a survey assigned to a linked device was visible in
 * Safari and absent from the installed app the moment the icon was added to the
 * Home Screen. Nothing had been deleted. There were two containers, and only
 * one of them held the token.
 *
 * Two consequences the app has to be able to speak about, and it cannot speak
 * about either without knowing which side of the wall it is on:
 *
 *   Installed and unlinked — the ONLY way a token used to arrive was a link
 *     opened from a text or an email, which opens in SAFARI. The installed app
 *     could therefore never be linked at all. It has to be told how to link
 *     itself, and that is the whole of D-019's pairing path.
 *   In Safari and unlinked — work done here is invisible to the installed app.
 *     A surveyor who fills in a day's answers in the browser and then installs
 *     the icon opens an empty app. So the warning has to land BEFORE the work,
 *     not after it.
 *
 * ── Why two signals ─────────────────────────────────────────────────────────
 * `display-mode: standalone` is the standard and is what Android and desktop
 * PWAs report. `navigator.standalone` is Apple's own, predates the media query
 * and is the one that is reliable on iOS — which is the only platform where the
 * storage partition actually bites. Both are consulted; either one is enough.
 *
 * `fullscreen` and `minimal-ui` count too. All three are launched-from-the-icon
 * modes with the icon's own container; only `browser` is the tab.
 *
 * Pure by construction: `standaloneFrom` takes the probe, so the branch is unit
 * tested in the node environment the rest of the suite runs in, and no test has
 * to fake a whole `window`.
 */

/** The two things a browser can be asked, both optional and both unreliable. */
export interface DisplayModeProbe {
  matchMedia?: ((query: string) => { matches: boolean }) | undefined;
  /** Apple's non-standard flag. `true` only inside a Home Screen web app. */
  navigatorStandalone?: unknown;
}

/** The display modes that mean "launched from the icon", not "a browser tab". */
const INSTALLED_MODES = ['standalone', 'fullscreen', 'minimal-ui'] as const;

/**
 * Is this probe describing an installed app?
 *
 * Defaults to FALSE when nothing can be determined, and that default is
 * load-bearing: the unknown case gets the install-first advice, which is
 * harmless to somebody who has already installed and is the whole point for
 * somebody who has not. Guessing "installed" would silently withdraw the one
 * warning that cost real data.
 */
export function standaloneFrom(probe: DisplayModeProbe): boolean {
  if (probe.navigatorStandalone === true) return true;

  const matchMedia = probe.matchMedia;
  if (typeof matchMedia !== 'function') return false;

  for (const mode of INSTALLED_MODES) {
    try {
      if (matchMedia(`(display-mode: ${mode})`).matches) return true;
    } catch {
      // An engine that does not understand the feature throws rather than
      // answering false. That is not an answer about installation, so keep
      // asking the others rather than taking it as one.
    }
  }
  return false;
}

/** `standaloneFrom` against the real browser. False anywhere there is no DOM. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return standaloneFrom({
    matchMedia: window.matchMedia.bind(window),
    navigatorStandalone: (navigator as { standalone?: unknown }).standalone,
  });
}

/**
 * Is this an iPhone or an iPad?
 *
 * Used for ONE thing: naming the button. "Share, then Add to Home Screen" is
 * exact on iOS and wrong everywhere else, and an instruction that names the
 * wrong menu is worse than a general one. iPadOS 13+ reports itself as a Mac,
 * so the touch-points test is what tells an iPad from a laptop.
 *
 * Never used to decide whether the warning appears — the storage partition is
 * an iOS fact, but a wrong guess about the platform must not be able to
 * suppress the warning on the platform that needs it.
 */
export function isAppleTouchDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = `${navigator.userAgent} ${navigator.platform ?? ''}`;
  if (/iPhone|iPad|iPod/.test(platform)) return true;
  return /Mac/.test(platform) && navigator.maxTouchPoints > 1;
}
